import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as hiveCore from '@gitbook/hive-core';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    FieldGroup,
    Heading,
    Label,
    TextInput,
} from '@gitbook/spine-react';

import { ContentReferenceInput, useContentReference } from '../../../Components';

export type InsertLinkModalKind = 'external' | 'space';

/**
 * Modal to insert a link (external or internal) in the ToC.
 */
export function InsertLinkModal(props: {
    onSubmit: (page: hiveCore.RevisionPageLink) => void;
    isOpen: boolean;
}): React.ReactElement {
    const { isOpen, onSubmit } = props;

    const [contentRef, setContentRef] = React.useState<api.ContentRef | null>(null);
    const [title, setTitle] = React.useState('');
    const isTitleDirtyRef = React.useRef(false);
    const [error, setError] = React.useState<string>('');

    // Resets modal state when it is closed.
    // Necessary because the modal is not unmounted on close.
    React.useEffect(() => {
        if (!isOpen) {
            setContentRef(null);
            setTitle('');
            isTitleDirtyRef.current = false;
            setError('');
        }
    }, [isOpen]);

    const submit = async () => {
        if (contentRef) {
            onSubmit(hiveCore.createPageLink({ title, target: contentRef }));
        } else {
            setError(
                'Something went wrong. We could not create that link. Please refresh and try again.'
            );
        }
    };

    return (
        <Dialog>
            <DialogHeader>
                <Heading>Insert link</Heading>
                <Description>Link to content or a URL to your table of contents.</Description>
            </DialogHeader>
            <DialogBody>
                <div className="flex flex-col gap-6">
                    <FieldGroup>
                        <Label>URL or content</Label>
                        <Description>{contentRefFieldDescription}</Description>
                        <ContentReferenceInput
                            size="large"
                            placeholder={contentRefFieldPlaceholder}
                            value={contentRef}
                            onChange={setContentRef}
                            accept={['space', 'page', 'anchor', 'url']}
                        />
                        <FieldError>{error}</FieldError>
                    </FieldGroup>

                    <React.Suspense
                        fallback={
                            <div className="flex flex-col gap-2">
                                <Label>Title</Label>
                                <Description>
                                    Describe the link content for your readers and also improved SEO
                                </Description>
                                <TextInput disabled value="Loading..." />
                            </div>
                        }
                    >
                        <ToCItemTitleInput
                            contentRef={contentRef}
                            title={title}
                            setTitle={setTitle}
                            isTitleDirtyRef={isTitleDirtyRef}
                        />
                    </React.Suspense>
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button label="Insert" kind="primary" onPress={submit} disabled={!contentRef} />
            </DialogFooter>
        </Dialog>
    );
}
export const contentRefFieldPlaceholder = 'e.g. Introduction, api/users, or gitbook.com/showcase';
export const contentRefFieldDescription =
    'You can link to a space, a page, and specific sections in a page by anchor';

function ToCItemTitleInput(props: {
    title: string;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    contentRef: api.ContentRef | null;
    isTitleDirtyRef: React.MutableRefObject<boolean>;
}) {
    const { title, setTitle, contentRef, isTitleDirtyRef } = props;
    const resolved = useContentReference(contentRef);

    // Init title with the resolved content reference title.
    React.useEffect(() => {
        if (contentRef && !isTitleDirtyRef.current && resolved.title) {
            setTitle(resolved.title);
        }
    }, [setTitle, resolved.title, contentRef, isTitleDirtyRef]);

    return (
        <FieldGroup>
            <Label>Title</Label>
            <Description>
                Describe the link content for your readers and also improved SEO
            </Description>
            <TextInput
                size="large"
                placeholder="e.g. Open API playground"
                value={title}
                onChange={(event) => {
                    isTitleDirtyRef.current = true;
                    setTitle(event.target.value);
                }}
                onClear={() => setTitle('')}
            />
        </FieldGroup>
    );
}
